import {
  PortaleId,
  Role,
  UserInternoCreateDto,
  UserInternoDto,
  UserInternoUpdateDto,
  UserRoleDto,
  getGetClientiQueryKey,
  getGetUsersQueryKey,
  useCreateUser,
  useGetPortali,
  useGetUsers,
  useUpdateUser,
} from "@/api";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import { addServerErrorsFactory } from "@/utils/addServerErrorsFactory";
import { languageOptions } from "@/utils/languages";
import { roles } from "@/utils/rolesAndPermissions";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { AxiosError } from "axios";
import { TFunction } from "i18next";
import { uniq } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UseFormSetError, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { withOpeningReset } from "../../utils/withOpeningReset";
import { SmallLoading } from "../Loading";

const columnHelper = createColumnHelper<UserInternoDto>();

function UtenteDialog({
  open = false,
  onClose,
  user,
}: {
  open?: boolean;
  onClose?: () => void;
  user?: UserInternoDto | null;
}) {
  const handleClose = () => {
    onClose?.();
  };
  const { t } = useTranslation();

  const { data: portaliData } = useGetPortali();

  const {
    register,
    watch,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isValid },
  } = useForm<UserInternoDto>({
    defaultValues: user || {},
  });

  const { saveAsync, isLoading, error } = useMutator(user, setError, t);

  const queryClient = useQueryClient();

  const onSubmit = async (data: UserInternoDto) => {
    console.log("data:", data);
    try {
      await saveAsync(data);
      queryClient.invalidateQueries({ queryKey: getGetUsersQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      onClose?.();
    } catch (err) {}
  };

  const selectedCommerciali = watch("commerciali");

  const { data: commercialiData } = useGetUsers({ role: "Commerciale" });
  const commerciali = (commercialiData?.data || []).filter(
    (c) => c.id !== user?.id,
  );

  const [selectedPortaliIds, setSelectedPortaliIds] = useState<PortaleId[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Role[]>([]);

  // form state -> react use state
  useEffect(() => {
    const portalIds = (user?.userRoles ?? [])
      .map((r) => r.portaleId)
      .filter((id) => id != null) as PortaleId[];
    setSelectedPortaliIds(uniq(portalIds));

    const roles = (user?.userRoles ?? [])
      .map((r) => r.role)
      .filter((role) => role != null) as Role[];
    setSelectedRoles(uniq(roles));
  }, [user]);

  // react use state -> form state
  useEffect(() => {
    if (selectedPortaliIds.length > 0 && selectedRoles.length > 0) {
      const newUserRoles: UserRoleDto[] = [];
      for (const portaleId of selectedPortaliIds) {
        for (const role of selectedRoles) {
          newUserRoles.push({
            portaleId: portaleId,
            role: role,
            userId: user?.id || 0,
          });
        }
      }

      setValue("userRoles", newUserRoles);
    }
  }, [selectedPortaliIds, selectedRoles]);

  const commColumns = useMemo(() => {
    const commColumns = [
      columnHelper.display({
        id: "commerciale_selected",
        header: "",
        size: 30,
        cell: ({ row }) => {
          return (
            <Box display="flex" flexDirection="row" justifyContent="center">
              <Checkbox
                checked={
                  selectedCommerciali?.find((sc) => sc.id === row.original.id)
                    ? true
                    : false
                }
                onChange={(e) => {
                  const selected = e.target.checked;
                  let newCommerciali: any[] = commerciali.filter((x) => {
                    return x.id === row.original.id
                      ? selected
                      : selectedCommerciali?.find((sc) => sc.id === x.id)
                      ? true
                      : false;
                  });
                  setValue("commerciali", newCommerciali);
                }}
              />
            </Box>
          );
        },
      }),
      columnHelper.accessor((u) => `${u.name} ${u.lastName}`, {
        header: t("utenteDialog.colonne.nome"),
        size: undefined,
      }),
    ].filter((x) => x);
    return commColumns as Exclude<(typeof commColumns)[number], boolean>[];
  }, [commerciali, selectedCommerciali, setValue, t]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        {user ? t("utenteDialog.titoloModifica") : t("utenteDialog.titoloCrea")}
      </DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="user-nome">
          {t("utenteDialog.campi.nome")}
        </InputLabel>
        <TextField
          id="user-nome"
          autoFocus
          {...register("name", {
            required: t("validazione.campoObbligatorio"),
            minLength: 1,
          })}
          error={!!errors.name?.message}
          helperText={errors.name?.message || null}
          fullWidth
        />

        <InputLabel htmlFor="user-cognome">
          {t("utenteDialog.campi.cognome")}
        </InputLabel>
        <TextField
          id="user-cognome"
          autoFocus
          {...register("lastName", {
            required: t("validazione.campoObbligatorio"),
            minLength: 1,
          })}
          error={!!errors.lastName?.message}
          helperText={errors.lastName?.message || null}
          fullWidth
        />

        <InputLabel htmlFor="user-email">
          {t("utenteDialog.campi.email")}
        </InputLabel>
        <TextField
          id="user-email"
          autoFocus
          {...register("email", {
            required: t("validazione.campoObbligatorio"),
            minLength: 1,
          })}
          error={!!errors.email?.message}
          helperText={errors.email?.message || null}
          fullWidth
        />

        <FormControl fullWidth error={!!errors.lingua?.message} sx={{ mt: 2 }}>
          <InputLabel id="user-lingua-label">
            {t("utenteDialog.lingua")}
          </InputLabel>
          <Select
            labelId="user-lingua-label"
            id="user-lingua"
            label={t("utenteDialog.lingua")}
            {...register("lingua", {
              required: t("validazione.campoObbligatorio"),
            })}
            defaultValue={user?.lingua || ""}
          >
            {languageOptions.map((language) => (
              <MenuItem key={language.code} value={language.code}>
                {language.getLabel(t)}
              </MenuItem>
            ))}
          </Select>
          {errors.lingua?.message && (
            <FormHelperText>{errors.lingua.message}</FormHelperText>
          )}
        </FormControl>

        <Box display="flex" flexDirection="row" columnGap={2}>
          <Box>
            <InputLabel>{t("utenteDialog.portali")}</InputLabel>
            <FormGroup sx={{ ml: 2 }}>
              {portaliData?.data?.map((portale, i) => {
                const checked = selectedPortaliIds.includes(portale.id!);

                return (
                  <FormControlLabel
                    key={i}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const selected = e.target.checked;

                          if (selected) {
                            setSelectedPortaliIds([
                              ...selectedPortaliIds,
                              portale.id,
                            ]);
                          } else {
                            setSelectedPortaliIds(
                              selectedPortaliIds.filter(
                                (id) => id !== portale.id,
                              ),
                            );
                          }
                        }}
                      />
                    }
                    label={portale.nome}
                  />
                );
              })}
            </FormGroup>
          </Box>

          <Box>
            <InputLabel>{t("utenteDialog.ruoli")}</InputLabel>
            <FormGroup sx={{ ml: 2 }}>
              {roles.map((role, i) => {
                if (role === "Cliente") {
                  return null;
                }

                const checked = selectedRoles.includes(role);

                return (
                  <FormControlLabel
                    key={i}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          const selected = e.target.checked;

                          if (selected) {
                            setSelectedRoles([...selectedRoles, role]);
                          } else {
                            setSelectedRoles(
                              selectedRoles.filter((r) => r !== role),
                            );
                          }
                        }}
                      />
                    }
                    label={role}
                  />
                );
              })}
              {errors.userRoles && (
                <FormHelperText error>
                  {errors.userRoles?.message}
                </FormHelperText>
              )}
            </FormGroup>
          </Box>
        </Box>

        {selectedRoles.includes("AreaManager") && (
          <>
            <InputLabel sx={{ marginTop: 2, marginBottom: -2 }}>
              {t("utenteDialog.commercialiAssociati")}
            </InputLabel>
            <Table columns={commColumns} data={commerciali} sortable={true} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={
            !isValid ||
            isLoading ||
            selectedPortaliIds.length === 0 ||
            selectedRoles.length === 0
          }
          onClick={handleSubmit(onSubmit)}
        >
          {user
            ? t("utenteDialog.azioni.salva")
            : t("utenteDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(UtenteDialog);

function useMutator(
  user: Partial<UserInternoDto> | null | undefined,
  setError: UseFormSetError<Partial<UserInternoDto>>,
  t: TFunction
): {
  isLoading: boolean;
  error: AxiosError<unknown, any> | null;
  saveAsync: (data: UserInternoDto) => Promise<any>;
} {
  const isNew = !user?.id;

  const create = useCreateUser({
    mutation: {
      onSuccess: () => {
        toast.success(t("utenteDialog.toast.creato"));
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error(t("errori.richiesta"));
      }),
    },
  });

  const update = useUpdateUser({
    mutation: {
      onSuccess: () => {
        toast.success(t("utenteDialog.toast.salvato"));
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error(t("errori.richiesta"));
      }),
    },
  });

  const { isLoading, error } = isNew ? create : update;

  return {
    isLoading,
    error,
    saveAsync: async (data: UserInternoDto) => {
      return isNew
        ? create.mutateAsync({ data: data as UserInternoCreateDto })
        : update.mutateAsync({
            id: user.id!,
            data: data as UserInternoUpdateDto,
          });
    },
  };
}
